import React, { useState, useEffect, useRef } from "react";
import { apiClient } from "../../apiclient/apiclient";
import {
  Plus,
  Trash2,
  Pencil,
  ArrowLeft,
  Save,
  RotateCcw,
  User,
  Phone,
  Building2,
  Mail,
} from "lucide-react";
import AddClient from "../ManageClient/AddClient";
import socket from "../../socket/socketClient";
import ThemedTable from "../../components/ThemedTable";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import PageHeader from "../../components/PageHeader";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const CreateOrders = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const isCoAdmin = user?.role === "coAdmin";

  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedDeliveryPersonId, setSelectedDeliveryPersonId] = useState("");
  const [orderDate, setOrderDate] = useState(new Date());

  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [editingItemId, setEditingItemId] = useState(null);

  const [isAddClientOpen, setIsAddClientOpen] = useState(false);
  const [qtyError, setQtyError] = useState("");

  const [rawProducts, setRawProducts] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [showDraftBanner, setShowDraftBanner] = useState(false);

  /* ================= DRAFT LOGIC ================= */
  useEffect(() => {
    const savedDraft = localStorage.getItem("order_draft");
    if (savedDraft) {
      setShowDraftBanner(true);
    }
  }, []);

  const saveDraft = () => {
    const draftData = {
      selectedClientId,
      selectedDeliveryPersonId,
      orderItems,
      payments,
      discount,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("order_draft", JSON.stringify(draftData));
    toast.success("Draft saved successfully");
    setShowDraftBanner(false);
  };

  const restoreDraft = () => {
    try {
      const savedDraft = localStorage.getItem("order_draft");
      if (!savedDraft) return;

      const data = JSON.parse(savedDraft);
      setSelectedClientId(data.selectedClientId || "");
      setSelectedDeliveryPersonId(data.selectedDeliveryPersonId || "");
      setOrderItems(data.orderItems || []);
      setPayments(data.payments || [{ mode: "Cash", amount: 0 }]);
      setDiscount(data.discount || 0);

      setShowDraftBanner(false);
      toast.success("Draft restored");
    } catch (err) {
      console.error("Failed to restore draft", err);
      toast.error("Failed to restore draft");
    }
  };

  const clearDraft = () => {
    localStorage.removeItem("order_draft");
    setShowDraftBanner(false);
    toast.info("Draft cleared");
  };

  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState("");
  const [isWhDropdownOpen, setIsWhDropdownOpen] = useState(false);
  const whDropdownRef = useRef(null);
  const [payments, setPayments] = useState([{ mode: "Cash", amount: 0 }]);

  const [discount, setDiscount] = useState(0);

  const [amountData, setAmountData] = useState({
    totalAmount: "",

    balanceAmount: "",
    paymentStatus: "COD",
  });

  const [itemForm, setItemForm] = useState({
    productId: "",
    productName: "",
    quantity: "",
    unitType: "",
    price: "",
    staffReference: "direct",
  });

  const [formData, setFormData] = useState({
    paymentMethod: "credit-card",
    notes: "",
    gstPercentage: 18,
  });

  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const inputClass =
    "border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full mt-1";

  const primaryBtn =
    "inline-flex items-center justify-center gap-2 rounded-md bg-orange-500 px-5 py-2 text-sm font-medium text-white " +
    "hover:bg-orange-600 transition " +
    "disabled:opacity-70 disabled:cursor-not-allowed";

  const secondaryBtn =
    "inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 " +
    "hover:bg-slate-100 transition";

  const labelClass = "text-xs font-medium text-slate-700";

  useEffect(() => {
    socket.on("order_created", (order) => {
      console.log("📦 New order created:", order);
    });

    return () => {
      socket.off("order_created");
    };
  }, []);
  // useEffect(() => {
  //   if (isCoAdmin && user?._id) {
  //     setSelectedDeliveryPersonId(user._id);
  //   }
  // }, [isCoAdmin, user]);

  // Listen for product stock updates from server and update local product list
  useEffect(() => {
    const handleProductUpdated = ({ productId, totalQuantity }) => {
      if (!productId) return;
      setRawProducts((prev) =>
        prev.map((p) => (p._id === productId ? { ...p, totalQuantity } : p)),
      );

      // Also update warehouseOptions if present
      setWarehouseOptions((prev) =>
        prev.map((w) =>
          w.id === productId ? { ...w, stock: totalQuantity } : w,
        ),
      );
    };

    socket.on("product_updated", handleProductUpdated);
    return () => socket.off("product_updated", handleProductUpdated);
  }, []);

  /* -------------------------------------------------------
     LOAD CLIENTS
  -------------------------------------------------------- */
  useEffect(() => {
    const loadClients = async () => {
      try {
        const res = await apiClient.get("/clients?lite=true");
        const sortedClients = (res.data?.clients || []).sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        setClients(sortedClients);
      } catch (err) {
        console.error("Failed to load clients", err);
      }
    };
    loadClients();
  }, []);

  /* -------------------------------------------------------
     LOAD DELIVERY PERSONS
  -------------------------------------------------------- */
  useEffect(() => {
    const loadDeliveryPersons = async () => {
      try {
        const res = await apiClient.get("/delivery-persons");
        const sortedDelivery = (res.data || []).sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        setDeliveryPersons(sortedDelivery);
      } catch (err) {
        console.error("Failed to load delivery persons", err);
      }
    };
    loadDeliveryPersons();
  }, []);

  /* -------------------------------------------------------
     LOAD STAFF (All Users)
  -------------------------------------------------------- */
  useEffect(() => {
    const loadStaff = async () => {
      try {
        const res = await apiClient.get("/all");
        const allUsers = res.data?.users || [];
        const sortedStaff = [
          { _id: "direct", name: "Direct Sales (No Agent)" },
          ...allUsers
            .filter((u) => u.name)
            .sort((a, b) => a.name.localeCompare(b.name)),
        ];

        setStaffList(sortedStaff);
      } catch (err) {
        console.error("Failed to load staff list", err);
        setStaffList([{ _id: "direct", name: "Direct Sales (No Agent)" }]);
      }
    };
    loadStaff();
  }, []);

  /* -------------------------------------------------------
     LOAD PRODUCTS + MERGE NAME (name + qty + unit)
  -------------------------------------------------------- */
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const res = await apiClient.get("/products");

        setRawProducts(res.data);

        const merged = res.data.map((p) => {
          const gstText = p.taxInclude ? "(Incl. GST)" : `(+${p.taxRate || 0}% GST)`;
          return {
            ...p,
            displayName: `${p.name} – ${p.quantityValue || 0} ${p.quantityUnit || ""} ${gstText}`,
          };
        });

        const map = new Map();
        merged.forEach((p) => {
          const key = `${p.name}-${p.quantityValue}-${p.quantityUnit}`;
          if (!map.has(key)) map.set(key, p);
        });

        const sortedProducts = Array.from(map.values()).sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        setProducts(sortedProducts);
      } catch (err) {
        console.error("Failed to load products", err);
      }
    };
    loadProducts();
  }, []);

  const SearchableSelect = ({
    options = [],
    value,
    onChange,
    placeholder,
    labelKey = "name",
  }) => {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    useEffect(() => {
      const selected = options.find((o) => o._id === value);
      if (selected) {
        setQuery(
          `${selected[labelKey]} ${selected.phone ? `(${selected.phone})` : ""}`,
        );
      } else {
        setQuery("");
      }
    }, [value, options]);

    const filtered = options.filter((o) =>
      `${o[labelKey]} ${o.phone || ""}`
        .toLowerCase()
        .includes(query.toLowerCase()),
    );

    return (
      <div className="relative" ref={ref}>
        <input
          type="text"
          value={query}
          placeholder={placeholder}
          className={inputClass}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
        />

        {open && (
          <ul className="absolute w-full bg-white border rounded mt-1 max-h-48 overflow-auto shadow z-50">
            {filtered.length === 0 && (
              <li className="p-2 text-sm text-gray-500">No results</li>
            )}

            {filtered.map((o) => (
              <li
                key={o._id}
                className="p-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => {
                  onChange(o._id);
                  setQuery(`${o[labelKey]} ${o.phone ? `(${o.phone})` : ""}`);
                  setOpen(false);
                }}
              >
                {o[labelKey]} {o.phone && `(${o.phone})`}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };
  /* -------------------------------------------------------
      CLIENT DETAILS
  -------------------------------------------------------- */
  const selectedClient = clients.find((c) => c._id === selectedClientId);

  const handleAddNewClient = (newClient) => {
    setClients([...clients, newClient]);
    setSelectedClientId(newClient._id);
  };

  /* -------------------------------------------------------
      ITEM QUANTITY CHANGE
  -------------------------------------------------------- */
  const handleItemChange = (e) => {
    const { name, value } = e.target;

    if (name === "quantity") {
      const qty = Number(value);
      if (!qty || qty <= 0) {
        setItemForm({ ...itemForm, quantity: "" });
        return;
      }

      const selectedWh = warehouseOptions.find(
        (w) => w.selectionKey === selectedWarehouseId,
      );
      const availableQty = selectedWh?.stock || 0;

      const alreadyOrdered = orderItems
        .filter(
          (it) =>
            it.productId === itemForm.productId &&
            it.warehouseId === selectedWh?.warehouseId &&
            (it.expiryDate ? it.expiryDate === selectedWh?.expiryDate : !selectedWh?.expiryDate),
        )
        .reduce((sum, it) => sum + it.quantity, 0);

      const remaining = availableQty - alreadyOrdered;

      if (qty > remaining) {
        setQtyError(`Only ${remaining} available in this warehouse`);
        return;
      }

      setQtyError("");
      setItemForm({ ...itemForm, quantity: qty });
      return;
    }

    setItemForm({ ...itemForm, [name]: value });
  };

  const addItem = () => {
    if (!itemForm.productId) {
      toast.error("Please select a product");
    }
    if (!selectedWarehouseId) {
      toast.error("Please select warehouse");
      return;
    }
    if (!itemForm.quantity) {
      toast.error("Please enter quantity");
      return;
    }

    const selectedProduct = products.find((p) => p._id === itemForm.productId);
    const selectedWarehouse = warehouseOptions.find(
      (w) => w.selectionKey === selectedWarehouseId,
    );

    const staffRef = itemForm.staffReference && itemForm.staffReference !== "direct" ? itemForm.staffReference : null;
    const staffRefName = staffList.find((s) => s._id === itemForm.staffReference)?.name || "Direct Sales (No Agent)";

    const itemToAdd = {
      id: Date.now(),
      productId: itemForm.productId,
      warehouseId: selectedWarehouse?.warehouseId || selectedWarehouseId,
      warehouseName: selectedWarehouse?.warehouseName || "Unknown",
      expiryDate: selectedWarehouse?.expiryDate || null,
      batchId: selectedWarehouse?.batchId || "",
      productName: selectedProduct.name,
      quantity: Number(itemForm.quantity),
      quantityValue: selectedProduct.quantityValue,
      quantityUnit: selectedProduct.quantityUnit,
      taxInclude: selectedProduct.taxInclude,
      taxRate: selectedProduct.taxRate,
      price: Number(itemForm.price),
      total: Number(itemForm.quantity) * Number(itemForm.price),
      staffReference: staffRef,
      staffReferenceName: staffRefName,
    };

    setOrderItems([...orderItems, itemToAdd]);

    // Clear product selection and reset item inputs after adding
    setItemForm({
      productId: "",
      productName: "",
      quantity: "",
      unitType: "",
      price: "",
      staffReference: "direct",
    });

    // clear editing flag after add/update
    setEditingItemId(null);

    setSelectedWarehouseId("");
    setQtyError("");
  };

  const removeItem = (id) => {
    setOrderItems(orderItems.filter((item) => item.id !== id));
  };

  const handleEditItem = (item) => {
    // populate form with selected item
    setItemForm({
      productId: item.productId,
      productName: item.productName,
      quantity: item.quantity,
      unitType: item.quantityUnit || item.unitType || "",
      price: item.price,
      staffReference: item.staffReference || "direct",
    });

    const selectedProd = products.find(p => p._id === item.productId);
    const warehouses = (selectedProd?.warehouses || []).map((w, idx) => ({
      selectionKey: `${w.location?._id || w.locationId || w.location || ""}_${w.expiryDate || "no-expiry"}_${idx}`,
      warehouseId: w.location?._id || w.locationId || w.location || "",
      warehouseName: w.location?.name || w.warehouseName || "",
      expiryDate: w.expiryDate,
      batchId: w.batchId,
      stock: Number(w.quantity || 0),
    }));
    setWarehouseOptions(warehouses);

    const matchedWh = warehouses.find(
      w => w.warehouseId === item.warehouseId && 
           (item.expiryDate ? w.expiryDate === item.expiryDate : !w.expiryDate)
    );

    setSelectedWarehouseId(matchedWh?.selectionKey || item.warehouseId);

    // remove item from list so it can be updated when user clicks Add/Update
    setOrderItems(orderItems.filter((it) => it.id !== item.id));
    setQtyError("");
    setEditingItemId(item.id);
  };
  useEffect(() => {
    const total = orderItems.reduce(
      (sum, item) => sum + item.quantity * item.price,
      0,
    );
    setAmountData((prev) => ({ ...prev, totalAmount: total }));
  }, [orderItems]);

  // Click outside listener for custom warehouse select dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (whDropdownRef.current && !whDropdownRef.current.contains(event.target)) {
        setIsWhDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /* -------------------------------------------------------
      BALANCE CALCULATION
  -------------------------------------------------------- */
  //  useEffect(() => {
  //   const total = Number(amountData.totalAmount || 0);
  //   const paid = Number(amountData.paidAmount || 0);

  //   const balance = Math.max(total - paid, 0);

  //   let status = "COD";
  //   if (paid >= total && total > 0) {
  //     status = "PAID";a
  //   } else if (paid > 0 && paid < total) {
  //     status = "PARTIAL";
  //   }

  //   setAmountData(prev => ({
  //     ...prev,
  //     balanceAmount: balance,
  //     paymentStatus: status,
  //   }));
  // }, [amountData.totalAmount, amountData.paidAmount]);

  const totalPaid = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);

  const payable = Math.max(amountData.totalAmount - discount, 0);

  const balance = Math.max(payable - totalPaid, 0);

  let paymentStatus = "unpaid";
  if (amountData.totalAmount === 0) {
    paymentStatus = "unpaid";
  } else if (payable === 0) {
    paymentStatus = "paid";
  } else if (totalPaid === 0) {
    paymentStatus = "unpaid";
  } else if (totalPaid >= payable) {
    paymentStatus = "paid";
  } else {
    paymentStatus = "partial";
  }

  /* -------------------------------------------------------
      SUBMIT ORDER
  -------------------------------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    if (!selectedClientId) {
      toast.error("Please select a client");
      return;
    }

    if (orderItems.length === 0) {
      toast.error("Add at least one item");
      return;
    }

    if (!selectedDeliveryPersonId && !isCoAdmin) {
      toast.error("Please select a delivery person");
      return;
    }

    const today = new Date();
    const isSameDate = 
      orderDate.getDate() === today.getDate() && 
      orderDate.getMonth() === today.getMonth() && 
      orderDate.getFullYear() === today.getFullYear();

    const finalOrderDate = isSameDate ? new Date().toISOString() : orderDate.toISOString();

    const payload = {
      clientId: selectedClientId,
      deliveryPersonId: selectedDeliveryPersonId,
      staffReference: orderItems[0]?.staffReference || null,
      paymentDetails: {
        totalAmount: amountData.totalAmount,
        discount,
        payments,
        paidAmount: totalPaid,
        balanceAmount: balance,
        paymentStatus,
      },
      items: orderItems,
      notes: formData.notes,
      orderDate: finalOrderDate,
      isBackdated: !isSameDate,
      status: "pending",
    };

    try {
      setIsSubmitting(true);
      const res = await apiClient.post("/orders", payload);

      // toast.success("Order created successfully 🎉");
      localStorage.removeItem("order_draft"); // ✅ CLEAR DRAFT ON SUCCESS

      // ✅ Role-based navigation
      if (user?.role === "coAdmin") {
        navigate("/agent/agent-dashboard");
      } else {
        navigate("/orders/view-order");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create order");
    } finally {
      setIsSubmitting(false); // 🔥 stop loader
      isSubmittingRef.current = false;
    }
  };

  return (
    <div className="mb-6 ">
      {/* PAGE HEADER (Mobile Only, Agent Only) */}
      {user?.role === "coAdmin" && (
        <div className="md:hidden mb-4">
          <PageHeader
            title="Create New Order"
            role="agent"
            leftContent={<ArrowLeft size={22} onClick={() => navigate(-1)} />}
          />
        </div>
      )}
      {/* PAGE HEADER for admin/other roles */}

      {/* Unified PAGE HEADER for admin/other roles (Settings style) */}
      {user?.role !== "delivery-boy" && user?.role !== "coAdmin" && (
        <div className="w-full border-b pt-3 px-6 pb-4 border-slate-200 bg-blue-50 mb-0">
          <h1 className="text-xl font-bold text-slate-800">Create New Order</h1>
          <p className="text-sm text-slate-500 mt-1">
            Select a client and add items
          </p>
        </div>
      )}

      {/* DRAFT RESTORATION BANNER */}
      {showDraftBanner && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 flex items-center justify-between animate-in fade-in slide-in-from-top-4 duration-500">
          <div className="flex items-center gap-2">
            <span className="flex h-2 w-2 rounded-full bg-amber-500 animate-pulse"></span>
            <p className="text-xs font-medium text-amber-800">
              You have an unsaved draft from{" "}
              {new Date(
                JSON.parse(localStorage.getItem("order_draft")).timestamp,
              ).toLocaleString()}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={restoreDraft}
              className="text-[10px] font-bold uppercase tracking-wider text-amber-700 hover:text-amber-900 flex items-center gap-1"
            >
              <RotateCcw size={12} /> Restore
            </button>
            <button
              onClick={clearDraft}
              className="text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-red-500 flex items-center gap-1"
            >
              <Trash2 size={12} /> Clear
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="px-3 sm:px-0 mb-0">
        {/* FORM GROUP — Client, Add Button, Delivery Person & Order Date */}
        <div className="px-6 border-b pb-6 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 items-end">
            {/* Client Selection & Add Button */}
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className={labelClass}>Client</label>
                <div className="mt-0">
                  <SearchableSelect
                    options={clients}
                    value={selectedClientId}
                    onChange={(id) => setSelectedClientId(id)}
                    placeholder="Search client..."
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsAddClientOpen(true)}
                className="shrink-0 flex items-center justify-center rounded-md bg-orange-500 text-white h-9 w-9 border-3 hover:bg-orange-600 focus:ring-2 focus:ring-amber-400 transition"
                title="Add New Client"
              >
                <Plus size={20} />
              </button>
            </div>

            {/* Delivery Person */}
            <div>
              <label className={labelClass}>Delivery Person</label>
              <div className="mt-0">
                <SearchableSelect
                  options={deliveryPersons}
                  value={selectedDeliveryPersonId}
                  onChange={(id) => setSelectedDeliveryPersonId(id)}
                  placeholder="Search delivery partner..."
                />
              </div>
            </div>

            {/* Order Date */}
            <div>
              <label className={labelClass}>Order Date</label>
              <div className="mt-0">
                <DatePicker
                  selected={orderDate}
                  onChange={(date) => setOrderDate(date)}
                  dateFormat="dd/MM/yyyy"
                  maxDate={new Date()}
                  className={inputClass}
                  wrapperClassName="w-full"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Selected Client Details Display (Track Order Style) */}
        {selectedClient && (
          <div className="w-full bg-blue-50 border-b p-5 border-slate-200 flex flex-wrap md:flex-nowrap justify-around items-center gap-6">
            <div className="flex flex-col gap-1.5 flex-1 min-w-[150px]">
              <div className="flex items-center gap-1.5 text-orange-500">
                <User size={14} />
                <span className="text-[10px] font-semibold uppercase tracking-wider">
                  Client Name
                </span>
              </div>
              <p
                className="font-bold text-slate-900 text-sm truncate"
                title={selectedClient.name}
              >
                {selectedClient.name}
              </p>
            </div>

            <div className="flex flex-col gap-1.5 flex-1 min-w-[150px]">
              <div className="flex items-center gap-1.5 text-orange-500">
                <Phone size={14} />
                <span className="text-[10px] font-semibold uppercase tracking-wider">
                  Phone
                </span>
              </div>
              <p className="font-bold text-slate-900 text-sm">
                {selectedClient.phone || "N/A"}
              </p>
            </div>

            <div className="flex flex-col gap-1.5 flex-1 min-w-[150px]">
              <div className="flex items-center gap-1.5 text-orange-500">
                <Building2 size={14} />
                <span className="text-[10px] font-semibold uppercase tracking-wider">
                  Company
                </span>
              </div>
              <p
                className="font-bold text-slate-900 text-sm truncate"
                title={selectedClient.companyName}
              >
                {selectedClient.companyName || "N/A"}
              </p>
            </div>

            <div className="flex flex-col gap-1.5 flex-1 min-w-[150px]">
              <div className="flex items-center gap-1.5 text-orange-500">
                <Mail size={14} />
                <span className="text-[10px] font-semibold uppercase tracking-wider">
                  Email
                </span>
              </div>
              <p
                className="font-bold text-slate-900 text-sm truncate"
                title={selectedClient.email}
              >
                {selectedClient.email || "N/A"}
              </p>
            </div>
          </div>
        )}

        {/* ORDER ITEMS */}
        <div className="px-6 pt-3 pb-4 border-b mb-0">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">
            ORDER ITEMS
          </h2>
          <div className="shrink-0"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 items-end mb-4">
            <div className="lg:col-span-7">
              <label className={labelClass}>Product</label>
              <div className="mt-0">
                <SearchableSelect
                  options={products}
                  value={itemForm.productId}
                  onChange={(id) => {
                    const p = products.find((prod) => prod._id === id);
                    if (!p) return;

                    const calcPrice = p.taxInclude === false ? (p.priceWithGst || p.price) : (p.price || 0);

                    setItemForm({
                      productId: id,
                      productName: p.name,
                      quantity: "",
                      unitType: p.quantityUnit,
                      price: Number(Number(calcPrice).toFixed(2)),
                    });

                    // ✅ Location + Expiry grouped stock
                    const warehouses = (p.warehouses || []).map((w, idx) => ({
                      selectionKey: `${w.location?._id || w.location || ""}_${w.expiryDate || "no-expiry"}_${idx}`,
                      warehouseId: w.location?._id || w.location || "",
                      warehouseName: w.location?.name || w.locationName || "",
                      warehouseAddress: w.location?.address || "",
                      expiryDate: w.expiryDate,
                      batchId: w.batchId,
                      stock: Number(w.quantity || 0),
                    }));

                    setWarehouseOptions(warehouses);
                    setSelectedWarehouseId("");
                    setQtyError("");
                  }}
                  placeholder="Search product..."
                  labelKey="displayName"
                />
              </div>
            </div>

            <div className="lg:col-span-5 relative" ref={whDropdownRef}>
              <label className={labelClass}>Warehouse & Expiry</label>
              
              {/* Trigger Button */}
              <button
                type="button"
                onClick={() => setIsWhDropdownOpen(!isWhDropdownOpen)}
                disabled={!itemForm.productId}
                className={`mt-1 w-full h-10 border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition flex justify-between items-center text-slate-800 text-left shadow-sm ${!itemForm.productId ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-slate-50/30"}`}
              >
                {(() => {
                  const selectedWh = warehouseOptions.find(w => w.selectionKey === selectedWarehouseId);
                  if (selectedWh) {
                    const alreadyOrdered = orderItems
                      .filter(
                        (it) =>
                          it.productId === itemForm.productId &&
                          it.warehouseId === selectedWh.warehouseId &&
                          (it.expiryDate ? it.expiryDate === selectedWh.expiryDate : !selectedWh.expiryDate),
                      )
                      .reduce((sum, it) => sum + it.quantity, 0);

                    const remainingStock = selectedWh.stock - alreadyOrdered;

                    let dateStr = "No Expiry";
                    if (selectedWh.expiryDate) {
                      try {
                        const d = new Date(selectedWh.expiryDate);
                        if (!isNaN(d.getTime())) {
                          dateStr = `Exp: ${d.toLocaleDateString()}`;
                        }
                      } catch (e) {}
                    }
                    const batchStr = selectedWh.batchId && selectedWh.batchId !== "N/A" ? selectedWh.batchId : "";

                    return (
                      <div className="flex justify-between items-center w-full min-w-0 pr-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="font-extrabold text-[#172554] truncate text-xs">
                            {selectedWh.warehouseName}
                          </span>
                          {batchStr && (
                            <span className="font-mono text-[9px] bg-slate-100 px-1 py-0.2 rounded border border-slate-200 text-slate-500 shrink-0">
                              {batchStr}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[10px] text-slate-500 font-bold shrink-0">{dateStr}</span>
                          <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[10px] font-black border border-blue-100 uppercase shrink-0">
                            {remainingStock} left
                          </span>
                        </div>
                      </div>
                    );
                  }
                  return <span className="text-slate-400">Select Warehouse & Expiry...</span>;
                })()}
                <span className="text-slate-400 text-[10px] ml-2 shrink-0">▼</span>
              </button>

              {/* Absolute Dropdown List */}
              {isWhDropdownOpen && (
                <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-60 overflow-y-auto z-[200] divide-y divide-slate-100 animate-in fade-in slide-in-from-top-1 duration-150">
                  {(() => {
                    // Group options by warehouseId and calculate minExpiry for sorting
                    const groups = {};
                    warehouseOptions.forEach((wh) => {
                      const key = wh.warehouseId || "unknown";
                      if (!groups[key]) {
                        groups[key] = {
                          name: wh.warehouseName || "Warehouse",
                          items: [],
                          minExpiry: Infinity,
                        };
                      }
                      groups[key].items.push(wh);

                      const expTime = wh.expiryDate ? new Date(wh.expiryDate).getTime() : Infinity;
                      if (expTime < groups[key].minExpiry) {
                        groups[key].minExpiry = expTime;
                      }
                    });

                    // Sort groups by nearest expiry date first
                    const groupList = Object.values(groups).sort((a, b) => a.minExpiry - b.minExpiry);

                    // Sort items chronologically inside each group and calculate total remaining stock
                    groupList.forEach((g) => {
                      g.items.sort((a, b) => {
                        const dateA = a.expiryDate ? new Date(a.expiryDate).getTime() : Infinity;
                        const dateB = b.expiryDate ? new Date(b.expiryDate).getTime() : Infinity;
                        return dateA - dateB;
                      });

                      g.totalRemaining = g.items.reduce((sum, wh) => {
                        const alreadyOrdered = orderItems
                          .filter(
                            (it) =>
                              it.productId === itemForm.productId &&
                              it.warehouseId === wh.warehouseId &&
                              (it.expiryDate ? it.expiryDate === wh.expiryDate : !wh.expiryDate),
                          )
                          .reduce((subSum, it) => subSum + it.quantity, 0);
                        return sum + Math.max(wh.stock - alreadyOrdered, 0);
                      }, 0);
                    });

                    if (groupList.length === 0) {
                      return (
                        <div className="p-3 text-slate-400 text-xs text-center italic">
                          No warehouse options found.
                        </div>
                      );
                    }

                    return groupList.map((group) => (
                      <div key={group.name} className="flex flex-col">
                        {/* Group Header Banner */}
                        <div className="bg-slate-50 px-3 py-1.5 font-extrabold text-[10px] text-[#172554] uppercase tracking-wider select-none border-b border-slate-200 flex justify-between items-center">
                          <span>{group.name}</span>
                          <span className="bg-[#172554] text-white px-2 py-0.5 rounded text-[9px] font-black shrink-0">
                            {group.totalRemaining} Total
                          </span>
                        </div>
                        
                        {/* Items inside this group */}
                        <div className="divide-y divide-slate-50">
                          {group.items.map((wh) => {
                            const alreadyOrdered = orderItems
                              .filter(
                                (it) =>
                                  it.productId === itemForm.productId &&
                                  it.warehouseId === wh.warehouseId &&
                                  (it.expiryDate ? it.expiryDate === wh.expiryDate : !wh.expiryDate),
                              )
                              .reduce((sum, it) => sum + it.quantity, 0);

                            const remainingStock = wh.stock - alreadyOrdered;

                            let dateStr = "No Expiry";
                            if (wh.expiryDate) {
                              try {
                                const d = new Date(wh.expiryDate);
                                if (!isNaN(d.getTime())) {
                                  dateStr = `Exp: ${d.toLocaleDateString()}`;
                                }
                              } catch (e) {}
                            }
                            const batchStr = wh.batchId && wh.batchId !== "N/A" ? wh.batchId : "";

                            return (
                              <div
                                key={wh.selectionKey}
                                onClick={() => {
                                  setSelectedWarehouseId(wh.selectionKey);
                                  setIsWhDropdownOpen(false);
                                }}
                                className="flex justify-between items-center px-3.5 py-2 hover:bg-slate-50/50 cursor-pointer text-xs transition-colors"
                              >
                                <div className="flex flex-col gap-0.5">
                                  <span className={`font-bold ${wh.expiryDate ? "text-slate-700" : "text-slate-500"}`}>
                                    {dateStr}
                                  </span>
                                  {batchStr && (
                                    <span className="text-[9px] font-mono text-slate-400 leading-none">
                                      Batch: {batchStr}
                                    </span>
                                  )}
                                </div>
                                <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[10px] font-black border border-blue-100 uppercase">
                                  {remainingStock} left
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>

            <div className="lg:col-span-3">
              <label className={labelClass}>Unit Price (₹)</label>
              <input
                type="text"
                className={`mt-1 ${inputClass}`}
                value={itemForm.price ?? ""}
                onChange={(e) =>
                  setItemForm({ ...itemForm, price: e.target.value })
                }
              />
            </div>

            <div className="lg:col-span-2">
              <label className={labelClass}>Quantity</label>
              <input
                type="text"
                name="quantity"
                value={itemForm.quantity}
                disabled={
                  selectedWarehouseId &&
                  warehouseOptions.find(
                    (w) => w.warehouseId === selectedWarehouseId,
                  )?.stock <= 0
                }
                onChange={handleItemChange}
                className={`mt-1 ${inputClass}`}
                min="1"
              />
            </div>

            <div className="lg:col-span-4">
              <label className={labelClass}>Reference Agent</label>
              <div className="mt-0">
                <SearchableSelect
                  options={staffList}
                  value={itemForm.staffReference}
                  onChange={(id) => setItemForm({ ...itemForm, staffReference: id })}
                  placeholder="Direct Sales (No Agent)"
                />
              </div>
            </div>

            <div className="lg:col-span-3">
              <button
                type="button"
                onClick={addItem}
                disabled={
                  !itemForm.productId ||
                  !selectedWarehouseId ||
                  warehouseOptions.find(
                    (w) => w.warehouseId === selectedWarehouseId,
                  )?.stock <= 0
                }
                className={`${primaryBtn} w-full`}
              >
                {editingItemId ? (
                  <>
                    <Pencil size={16} /> Update
                  </>
                ) : (
                  <>
                    <Plus size={18} /> Add Item
                  </>
                )}
              </button>
            </div>
          </div>

          {qtyError && (
            <p className="text-red-500 text-xs font-medium px-1">{qtyError}</p>
          )}

          {/* ================= ORDER ITEMS LIST ================= */}
          {orderItems.length > 0 && (
            <div className="rounded-xl">
              {/* ✅ DESKTOP — ThemedTable */}
              <div className="hidden md:block w-full overflow-x-auto">
                <ThemedTable className="min-w-[800px] text-sm">
                  <thead className="20 bg-slate-50 border-b border-slate-200 text-slate-600 uppercase text-xs tracking-wide">
                    <tr>
                      <th className="px-3 py-3 text-left">Product</th>
                      <th className="px-3 py-3 text-left">Warehouse</th>
                      <th className="px-3 py-3 text-left">Reference</th>
                      <th className="px-3 py-3 text-center">Qty</th>
                      <th className="px-3 py-3 text-center">Price</th>
                      <th className="px-3 py-3 text-center">GST (%)</th>
                      <th className="px-3 py-3 text-center">Total</th>
                      <th className="px-3 py-3 text-center">Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {orderItems.map((item) => (
                      <tr
                        key={item.id}
                        className="hover:bg-amber-50 transition bg-white border-b"
                      >
                        <td className="px-3 py-3 font-medium">
                          {item.productName} – {item.quantityValue}{" "}
                          {item.quantityUnit}
                        </td>
                        <td className="px-3 py-3">
                          {item.warehouseName}
                          {item.expiryDate && ` (Exp: ${new Date(item.expiryDate).toLocaleDateString()})`}
                          {item.batchId && item.batchId !== "N/A" && ` [Batch: ${item.batchId}]`}
                        </td>
                        <td className="px-3 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${item.staffReference && item.staffReference !== "direct" ? "bg-purple-100 text-purple-800" : "bg-slate-100 text-slate-600"}`}>
                            {item.staffReferenceName || staffList.find((s) => s._id === item.staffReference)?.name || "Direct Sales (No Agent)"}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">
                            {item.quantity}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          ₹{item.taxInclude
                            ? (((item.quantity * item.price) * 100) / (100 + (item.taxRate || 0))).toFixed(2)
                            : (item.quantity * item.price).toFixed(2)}
                        </td>
                        <td className="px-3 py-3 text-center text-red-600">
                          {item.taxRate || 0}% (₹{item.taxInclude
                            ? ((item.quantity * item.price) - (((item.quantity * item.price) * 100) / (100 + (item.taxRate || 0)))).toFixed(2)
                            : ((item.quantity * item.price) * (item.taxRate || 0) / 100).toFixed(2)})
                        </td>
                        <td className="px-3 py-3 text-center font-semibold text-blue-900">
                          ₹{item.taxInclude
                            ? (item.quantity * item.price).toFixed(2)
                            : ((item.quantity * item.price) * (1 + (item.taxRate || 0) / 100)).toFixed(2)}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <div className="flex justify-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleEditItem(item)}
                              className="p-1.5 rounded-full border-2 border-[#172554] text-white bg-[#172554] hover:bg-white hover:text-[#172554] transition-colors"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeItem(item.id)}
                              className="p-1.5 rounded-full border-2 border-orange-500 bg-orange-500 text-white hover:bg-white hover:text-orange-500 transition-colors"
                              title="Remove item"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </ThemedTable>
              </div>

              {/* ✅ MOBILE — Card Layout */}
              <div className="md:hidden space-y-3">
                {orderItems.map((item) => (
                  <div
                    key={item.id}
                    className="border rounded-lg p-4 bg-white shadow-sm"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-slate-800">
                        {item.productName} – {item.quantityValue}{" "}
                        {item.quantityUnit}{" "}
                        <span className="text-xs text-slate-500 font-normal">
                          {item.taxInclude ? "(Incl. GST)" : `(+${item.taxRate || 0}% GST)`}
                        </span>
                      </h4>
                      <span className="text-sm font-bold text-blue-700">
                        ₹{item.taxInclude
                          ? (item.quantity * item.price).toFixed(2)
                          : ((item.quantity * item.price) * (1 + (item.taxRate || 0) / 100)).toFixed(2)}
                      </span>
                    </div>

                    <div className="text-xs text-slate-600 space-y-1">
                      <div>
                        <b>Warehouse:</b> {item.warehouseName}
                        {item.expiryDate && ` (Exp: ${new Date(item.expiryDate).toLocaleDateString()})`}
                        {item.batchId && item.batchId !== "N/A" && ` [Batch: ${item.batchId}]`}
                      </div>
                      <div>
                        <b>Reference:</b>{" "}
                        <span className={`font-semibold ${item.staffReference && item.staffReference !== "direct" ? "text-purple-700" : "text-slate-600"}`}>
                          {item.staffReferenceName || staffList.find((s) => s._id === item.staffReference)?.name || "Direct Sales (No Agent)"}
                        </span>
                      </div>
                      <div>
                        <b>Qty:</b> {item.quantity}
                      </div>
                      <div>
                        <b>Price (Base Total):</b> ₹{item.taxInclude
                          ? (((item.quantity * item.price) * 100) / (100 + (item.taxRate || 0))).toFixed(2)
                          : (item.quantity * item.price).toFixed(2)}
                      </div>
                      <div>
                        <b>GST ({item.taxRate || 0}%):</b> ₹{item.taxInclude
                          ? ((item.quantity * item.price) - (((item.quantity * item.price) * 100) / (100 + (item.taxRate || 0)))).toFixed(2)
                          : ((item.quantity * item.price) * (item.taxRate || 0) / 100).toFixed(2)}
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => handleEditItem(item)}
                        className="p-1.5 rounded-full border-2 border-[#172554] text-white bg-[#172554] hover:bg-white hover:text-[#172554] transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>

                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        className="p-1.5 rounded-full border-2 border-orange-500 bg-orange-500 text-white hover:bg-white hover:text-orange-500 transition-colors"
                        title="Remove item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* PAYMENT */}
        {/* PAYMENT SECTION */}
        <div className="grid grid-cols-1 lg:grid-cols-2 mb-0">
          {/* LEFT — PAYMENTS COLLECTED */}
          <div className="bg-white px-6 pt-3 pb-6 border-b lg:border-b-0 lg:border-e">
            {/* HEADER */}
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">
                  PAYMENTS COLLECTED
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Add multiple payment modes
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setPayments([...payments, { mode: "Cash", amount: "" }])
                }
                className="bg-orange-500 hover:bg-orange-600 
                 text-white px-3 py-2 text-sm font-medium gap-2
                 rounded-md flex items-center 
                 justify-center transition"
              >
                <Plus size={18} /> Add Mode
              </button>
            </div>

            {/* PAYMENT ROWS */}
            {payments.map((p, index) => (
              <div key={index} className="flex items-end gap-3 mb-3">
                {/* MODE */}
                <div className="flex flex-col">
                  <label className={labelClass}>Mode</label>
                  <select
                    className={inputClass}
                    value={p.mode}
                    onChange={(e) => {
                      const updated = [...payments];
                      updated[index].mode = e.target.value;
                      setPayments(updated);
                    }}
                  >
                    <option>Cash</option>
                    <option>UPI</option>
                    <option>Card</option>
                    <option>Bank</option>
                  </select>
                </div>

                {/* AMOUNT */}
                <div className="flex flex-col flex-1">
                  <label className={labelClass}>Amount</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={p.amount}
                    onChange={(e) => {
                      const updated = [...payments];
                      updated[index].amount = e.target.value;
                      setPayments(updated);
                    }}
                  />
                </div>

                {/* REMOVE */}
                <button
                  type="button"
                  onClick={() => {
                    const updated = payments.filter((_, i) => i !== index);
                    setPayments(updated);
                  }}
                  className="h-10 w-10 rounded-lg flex items-center justify-center hover:bg-red-100 text-red-500 transition"
                >
                  ✕
                </button>
              </div>
            ))}

            {/* DISCOUNT SECTION */}
            <div className="mt-5 pt-2 border-t border-slate-200">
              <label className={labelClass}>₹ Discount Amount</label>
              <input
                type="text"
                value={discount}
                onChange={(e) => setDiscount(Number(e.target.value || 0))}
                className={`mt-1 ${inputClass}`}
              />
            </div>
          </div>

          {/* RIGHT — ORDER SUMMARY */}
          <div className="px-6 pt-4 pb-6 flex flex-col justify-center">
            <p className="text-lg font-semibold text-slate-800 mb-3">
              ORDER SUMMARY
            </p>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Total Bill</span>
                <span>₹{amountData.totalAmount}</span>
              </div>

              <div className="flex justify-between text-red-400">
                <span>Discount Applied</span>
                <span>- ₹{discount}</span>
              </div>
            </div>

            <hr className="my-4 border-slate-700" />

            <div className="flex justify-between items-center mb-3">
              <span className="text-lg font-semibold">Payable</span>

              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-amber-400">
                  ₹{payable}
                </span>

                {paymentStatus === "paid" && (
                  <span className="bg-green-600 text-xs px-3 py-1 rounded-full">
                    PAID
                  </span>
                )}
              </div>
            </div>

            {/* STATUS BAR */}
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden mb-3">
              <div
                className={`h-full transition-all ${paymentStatus === "paid"
                    ? "bg-green-500"
                    : paymentStatus === "partial"
                      ? "bg-yellow-400"
                      : "bg-red-500"
                  }`}
                style={{ width: `${payable > 0 ? Math.min(100, (totalPaid / payable) * 100) : 0}%` }}
              />
            </div>

            <div className="flex justify-between text-sm">
              <span>Balance Due</span>
              <span className="text-green-400 font-semibold">₹{balance}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-row justify-end gap-3 px-6 pt-4 border-t border-b pb-4 mb-48">
          <button
            type="reset"
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-6 py-2 rounded-md transition-colors text-sm border border-slate-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={saveDraft}
            className="bg-[#172554] text-white font-semibold px-6 py-2 rounded-md transition-colors text-sm flex items-center gap-2 disabled:opacity-60"
          >
            <Save size={16} /> Save Draft
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2 rounded-md transition-colors text-sm flex items-center gap-2 disabled:opacity-60"
          >
            {isSubmitting ? (
              <Loader text="Creating..." inline />
            ) : (
              <>Create Order</>
            )}
          </button>
        </div>
      </form>

      <AddClient
        isOpen={isAddClientOpen}
        onClose={() => setIsAddClientOpen(false)}
        onAddClient={handleAddNewClient}
      />
    </div>
  );
};

export default CreateOrders;
